import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../services/api'
import type { SaasSubscriptionState } from '../services/api'
import { useStoreOwner } from '../store/useStoreOwner'
import { useOwnerBillingRevision } from '../lib/ownerBillingRefresh'

/**
 * 月額 SaaS 利用料の契約状態と、移行の告知 (#1689)。
 *
 * 取得に失敗したときは `null` を返す（バナーを出さない）。**未解禁のあいだは
 * backend が 404 を返す**ので、失敗が既定の状態になる。ここでエラー表示をすると
 * 全店舗画面に無関係な警告が出る。
 *
 * uid と一緒に保持して、別オーナーへ切り替えた直後に前のオーナーの状態を出さない
 * (`useOwnerSuspension` と同じ形)。
 */
export function useSaasSubscription() {
  const isStoreLoggedIn = useStoreOwner((s) => s.isStoreLoggedIn)
  const uid = useStoreOwner((s) => s.firebaseUser?.uid)
  const rev = useOwnerBillingRevision()
  const [record, setRecord] = useState<{ uid: string; value: SaasSubscriptionState } | null>(null)
  const [loading, setLoading] = useState(true)

  // 遅れて返ってきた応答で state を壊さないための世代番号。オーナーを切り替えた
  // 直後に前のオーナーの応答が着くと、uid が一致せず **state が永久に null** に
  // なり、申込み画面が「受け付けていません」を出し続ける。
  const generation = useRef(0)

  const reload = useCallback(async () => {
    if (!isStoreLoggedIn || !uid) {
      setLoading(false)
      return
    }
    const mine = ++generation.current
    const targetUid = uid
    setLoading(true)
    try {
      const value = await api.getSaasSubscription()
      if (generation.current === mine) setRecord({ uid: targetUid, value })
    } catch {
      // 未解禁 (404) が既定。通信エラーと区別する意味が無いので同じ扱いにする。
      if (generation.current === mine) setRecord(null)
    } finally {
      if (generation.current === mine) setLoading(false)
    }
  }, [isStoreLoggedIn, uid])

  // rev はバナーのための依存。申込み画面が `reload()` を呼んでも効くのは
  // 自分の instance だけで、常時表示されているバナーの instance には届かない。
  useEffect(() => {
    void reload()
  }, [reload, rev])

  const state = record && uid && record.uid === uid ? record.value : null
  return { state, loading, reload }
}
